import { useState, type FormEvent } from "react";
import type { ApiToken } from "../types";

export function TokensPage({
  tokens,
  onCreate,
  onRevoke,
  onRefresh
}: {
  tokens: ApiToken[];
  onCreate: (name: string) => Promise<string>;
  onRevoke: (id: string) => Promise<void>;
  onRefresh: () => Promise<void>;
}) {
  const [name, setName] = useState("CI token");
  const [secret, setSecret] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setSecret("");
    try {
      const raw = await onCreate(name);
      setSecret(raw);
      setName("CI token");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="topbar">
        <div>
          <h1>API tokens</h1>
          <p>
            Long-lived staff tokens for automation. Secrets are shown once at creation.
          </p>
        </div>
        <button type="button" className="btn btn-ghost" onClick={() => onRefresh()}>
          Refresh
        </button>
      </div>

      <form className="panel-form" onSubmit={submit}>
        <div className="panel-form-title">Create token</div>
        {error ? <div className="error-banner">{error}</div> : null}
        {secret ? (
          <div className="success-banner">
            Copy now — this secret will not be shown again:
            <div className="secret-box mono">{secret}</div>
          </div>
        ) : null}
        <div className="form-grid">
          <div className="field">
            <label>Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
        </div>
        <button className="btn btn-primary btn-inline" disabled={busy} type="submit">
          {busy ? "Creating…" : "Generate token"}
        </button>
      </form>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Prefix</th>
              <th>Created</th>
              <th>Last used</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id}>
                <td>{t.name}</td>
                <td className="mono">{t.prefix}…</td>
                <td className="mono faint">
                  {new Date(t.createdAt).toLocaleString()}
                </td>
                <td className="mono faint">
                  {t.lastUsedAt
                    ? new Date(t.lastUsedAt).toLocaleString()
                    : "never"}
                </td>
                <td>
                  <button
                    type="button"
                    className="link-btn danger"
                    onClick={() => {
                      if (confirm("Revoke this token?")) void onRevoke(t.id);
                    }}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
            {tokens.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty">
                  No API tokens yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </>
  );
}
